<template>
  <div
    :class="`margin-bottom-16 border-radius-4 flex-start-between background-${
      !!item.error ? 'error-50' : 'surface'
    }`"
  >
    <Checkbox
      :label="item.caption"
      :value="item.isSelect"
      name="isSelect"
      rowClass="flex-start-start mobile-flex-direction-column"
      labelClass="text-justify"
      hoverClass=""
      disabledClass=""
      labelTag="div"
      :loading="changeStatusLoading"
      :disabled="!hasItemPermission(['EVIDENCE_SELECT'])"
      @input="handleSelect(item)"
      @change="handleChangeIsSelect"
    />
    <div class="flex-end">
      <DropdownModal
        v-if="item.error && hasSample"
        class="margin-x-8"
        modalClass="left-0 top-26 box-shadow--modal"
      >
        <template #click-area>
          <span class="color-error icon-hover">
            <mdicon name="alert-circle-outline" size="24" />
          </span>
        </template>
        <template #content>
          <CompetencyErrorForm mode="EDIT" :evidenceItem="item" />
        </template>
      </DropdownModal>
      <DropdownModal
        v-if="item.isSelect && (item.behavioralWitness || !viewOnly)"
        class="margin-x-8"
        modalClass="left-0 top-26 box-shadow--modal"
      >
        <template #click-area>
          <span
            v-if="item.behavioralWitness && hasSample"
            class="color-primary icon-hover"
          >
            <mdicon name="account-eye" size="24" />
          </span>
          <span v-else class="color-complementary-200 icon-hover">
            <mdicon name="account-eye-outline" size="24" />
          </span>
        </template>
        <template #content>
          <BehavioralWitnessForm
            :mode="item.behavioralWitness && hasSample ? 'EDIT' : 'ADD'"
            :evidenceItem="item"
            :indicatorCaption="indicatorCaption"
          />
        </template>
      </DropdownModal>
      <MenuPopupWrapper
        v-if="itemButtons.some(({ show }) => show)"
        iconClass="color-complementary-200 position-relative"
        menuClass="left-0 top-12 z-index-1 padding-top-24"
        :items="itemButtons"
        :permissions="['admin']"
        :showMenu="itemMenuId === item.id"
        :rowItem="item"
        @click="showItemMenu"
        @close="closeItemMenu"
      />
      <BehavioralWitnessListPin
        :visible="isBehavioralWitnessListPin"
        :evidenceId="item.evidenceDataId"
        @handleClose="handleCloseHistoryBehavioralWitnessPin"
      />
      <Modal
        :show="isAddErrorModal"
        :title="$lang.addError"
        :closeOnBackdrop="false"
        :hasClose="false"
        :hasFooter="false"
        size="sm"
        @close="handleCloseErrorModal"
      >
        <template #body>
          <CompetencyErrorForm
            :evidenceItem="item"
            :hasCancelButton="true"
            @handleClose="handleCloseErrorModal"
          />
        </template>
      </Modal>
      <Modal
        :show="isAddBehavioralWitnessModal"
        :title="$lang.AddBehavioralWitness"
        :closeOnBackdrop="false"
        :hasClose="false"
        :hasFooter="false"
        size="sm"
        @close="handleCloseBehavioralWitnessModal"
      >
        <template #body>
          <BehavioralWitnessForm
            :evidenceItem="item"
            :indicatorCaption="indicatorCaption"
            :hasCancelButton="true"
            @handleClose="handleCloseBehavioralWitnessModal"
          />
        </template>
      </Modal>
      <Modal
        :show="isRemoveErrorModal"
        :title="$lang.removeError"
        :closeOnBackdrop="false"
        :hasClose="false"
        :okButton="true"
        :okButtonText="$lang.remove"
        okButtonVariant="filled-error"
        size="sm"
        :ok="handleRemoveError"
        @close="handleCloseRemoveErrorModal"
      >
        <template #body>
          <div class="margin-bottom-24 font-size-14 color-complementary-300">
            {{ $lang.removeSureMessage }}
          </div>
        </template>
      </Modal>
      <Modal
        :show="isRemoveBehavioralWitnessModal"
        :title="$lang.removeBehavioralWitness"
        :closeOnBackdrop="false"
        :hasClose="false"
        :okButton="true"
        :okButtonText="$lang.remove"
        okButtonVariant="filled-error"
        size="sm"
        :ok="handleRemoveBehavioralWitness"
        @close="handleCloseRemoveBehavioralWitnessModal"
      >
        <template #body>
          <div class="margin-bottom-24 font-size-14 color-complementary-300">
            <div>{{ $lang.removeBehavioralWitnessMessage }}</div>
            <div>
              {{ $lang.removeSureMessage }}
            </div>
          </div>
        </template>
      </Modal>
      <Modal
        :show="isChangeStatusConfirmModal"
        :title="$lang.evidenceCancel"
        :closeOnBackdrop="false"
        :hasClose="false"
        :okButton="true"
        :okButtonText="$lang.yes"
        okButtonVariant="filled-error"
        size="sm"
        :ok="handleSubmitChangeStatusConfirmModal"
        @close="handleCloseChangeStatusConfirmModal"
      >
        <template #body>
          <div class="margin-bottom-24 font-size-14 color-complementary-300">
            {{ $lang.evidenceCancelMessage }}
          </div>
        </template>
      </Modal>
    </div>
  </div>
</template>

<script>
import inputMixin from "@/mixin/inputMixin.vue";
import { MenuPopupWrapper, DropdownModal, Modal } from "@/components/re-design";
import { Checkbox } from "@/components/re-design/forms";
import BehavioralWitnessForm from "../behavioral-witness/BehavioralWitnessForm.vue";
import CompetencyErrorForm from "../competency/CompetencyErrorForm.vue";
import BehavioralWitnessListPin from "../behavioral-witness/BehavioralWitnessListPin.vue";
import { pathOr } from "ramda";
import { computed, inject, provide, ref, toRefs } from "vue";
import { generateError, generateLang, toastAlert } from "@/shared/utils";
import {
  removeEvidenceData,
  removeEvidenceDataLog,
  setCenterExecuteCaseCompetencyIndicatorEvidence,
} from "@/modules/kanoon/services";
export default {
  mixins: [inputMixin],
  components: {
    Checkbox,
    MenuPopupWrapper,
    DropdownModal,
    Modal,
    BehavioralWitnessForm,
    CompetencyErrorForm,
    BehavioralWitnessListPin,
  },
  props: {
    item: {
      type: Object,
      default: null,
    },
    indicatorCaption: {
      type: String,
      default: "",
    },
  },
  setup(props) {
    // generate props data
    const { item } = toRefs(props);

    const centerExecuteId = inject("CENTER_EXECUTE_ID");
    const caseId = inject("CASE_ID");
    const competencyId = inject("COMPETENCY_ID");
    const indicatorId = inject("INDICATOR_ID");
    const getIndicators = inject("GET_INDICATORS");
    const hasItemPermission = inject("hasItemPermission");
    const hasSample = inject("HAS_SAMPLE");
    const viewOnly = inject("VIEW_ONLY");

    // handle menu popup
    const itemMenuId = ref(null);
    const showItemMenu = (item) => {
      const id = pathOr(null, ["id"], item);
      itemMenuId.value = itemMenuId.value === id ? null : id;
    };
    const closeItemMenu = () => (itemMenuId.value = null);

    // handle change status
    const changeStatusLoading = ref(false);
    const handleChangeStatus = async () => {
      try {
        changeStatusLoading.value = true;
        const evidenceId = pathOr(null, ["id"], item.value);
        const isSelect = pathOr(false, ["isSelect"], item.value);
        const errorCode = pathOr(null, ["error", "code"], item.value);
        const errorDescription = pathOr(
          null,
          ["error", "description"],
          item.value
        );
        await setCenterExecuteCaseCompetencyIndicatorEvidence({
          centerExecuteId: centerExecuteId.value,
          caseId: caseId.value,
          competencyId: competencyId.value,
          indicatorId: indicatorId.value,
          evidenceId,
          errorCode,
          errorDescription,
          behavioralWitness: null,
          isSelect,
        });
      } catch (error) {
        // we don't want to show the error
      } finally {
        changeStatusLoading.value = false;
      }
    };

    // handle behavioral witness history list
    const isBehavioralWitnessListPin = ref(false);
    const handleOpenHistoryBehavioralWitnessPin = () => {
      isBehavioralWitnessListPin.value = true;
    };
    const handleCloseHistoryBehavioralWitnessPin = () => {
      isBehavioralWitnessListPin.value = false;
    };

    // handle add behavioral witness
    const isAddBehavioralWitnessModal = ref(false);
    const handleOpenBehavioralWitnessModal = () => {
      isAddBehavioralWitnessModal.value = true;
    };
    const handleCloseBehavioralWitnessModal = () => {
      isAddBehavioralWitnessModal.value = false;
    };

    // handle remove behavioral witness
    const isRemoveBehavioralWitnessModal = ref(false);
    const handleOpenRemoveBehavioralWitnessModal = () => {
      isRemoveBehavioralWitnessModal.value = true;
    };
    const handleCloseRemoveBehavioralWitnessModal = () => {
      isRemoveBehavioralWitnessModal.value = false;
    };
    const handleRemoveBehavioralWitness = async () => {
      try {
        const { evidenceDataId } = item.value;
        await removeEvidenceData(evidenceDataId);
        handleCloseRemoveBehavioralWitnessModal();
        getIndicators();
      } catch (error) {
        generateError(error);
      }
    };

    // handle add error
    const isAddErrorModal = ref(false);
    const handleOpenErrorModal = () => {
      isAddErrorModal.value = true;
    };
    const handleCloseErrorModal = () => {
      isAddErrorModal.value = false;
    };

    // handle remove error
    const isRemoveErrorModal = ref(false);
    const handleOpenRemoveErrorModal = () => {
      isRemoveErrorModal.value = true;
    };
    const handleCloseRemoveErrorModal = () => {
      isRemoveErrorModal.value = false;
    };
    const handleRemoveError = async () => {
      try {
        const { evidenceDataId } = item.value;
        await removeEvidenceDataLog(evidenceDataId, centerExecuteId.value);
        handleCloseRemoveErrorModal();
        getIndicators();
      } catch (error) {
        generateError(error);
      }
    };

    // handle change status confirmation
    const isChangeStatusConfirmModal = ref(false);
    const handleOpenChangeStatusConfirmModal = () => {
      isChangeStatusConfirmModal.value = true;
    };
    const handleCloseChangeStatusConfirmModal = (
      shouldGetIndicators = true
    ) => {
      isChangeStatusConfirmModal.value = false;
      if (shouldGetIndicators) {
        getIndicators();
      }
    };
    const handleSubmitChangeStatusConfirmModal = async () => {
      item.isSelect = !item.isSelect;
      handleCloseChangeStatusConfirmModal(false);
      await handleChangeStatus();
      getIndicators();
    };

    // handle select and trace evidence checkbox
    const handleSelect = async (item) => (item.isSelect = !item.isSelect);
    const handleChangeIsSelect = async (value) => {
      if (value) {
        // when selected
        await handleChangeStatus();
        getIndicators();
      } else {
        // when deselected
        handleOpenChangeStatusConfirmModal();
      }
    };

    // items for menu popup
    const itemButtons = computed(() => [
      {
        caption: generateLang(["addError"]),
        show:
          !item.value.error &&
          hasItemPermission(["ERROR_ADD"]) &&
          hasSample.value,
        permissions: ["admin", "edit"],
        loading: false,
        click: handleOpenErrorModal,
      },
      {
        caption: generateLang(["AddBehavioralWitness"]),
        show:
          !item.value.behavioralWitness &&
          item.value.isSelect &&
          hasItemPermission(["SAMPLE_ADD"]) &&
          hasSample.value,
        permissions: ["admin", "edit"],
        loading: false,
        click: handleOpenBehavioralWitnessModal,
      },
      {
        caption: generateLang(["removeError"]),
        show:
          item.value.error &&
          hasItemPermission(["ERROR_REMOVE"]) &&
          hasSample.value,
        permissions: ["admin", "edit"],
        loading: false,
        click: handleOpenRemoveErrorModal,
      },
      {
        caption: generateLang(["removeBehavioralWitness"]),
        show:
          item.value.behavioralWitness &&
          hasItemPermission(["SAMPLE_REMOVE"]) &&
          hasSample.value,
        permissions: ["admin", "edit"],
        loading: false,
        click: handleOpenRemoveBehavioralWitnessModal,
      },
      {
        caption: generateLang(["historyBehavioralWitness"]),
        show: item.value.behavioralWitness,
        permissions: ["admin", "edit"],
        loading: false,
        click: handleOpenHistoryBehavioralWitnessPin,
      },
    ]);

    provide("EVIDENCE_ID", item.value.id);

    // return data to be used in template
    return {
      pathOr,
      item,
      itemButtons,
      itemMenuId,
      showItemMenu,
      closeItemMenu,
      handleSelect,
      isBehavioralWitnessListPin,
      handleOpenHistoryBehavioralWitnessPin,
      handleCloseHistoryBehavioralWitnessPin,
      isAddBehavioralWitnessModal,
      handleCloseBehavioralWitnessModal,
      isAddErrorModal,
      handleCloseErrorModal,
      isRemoveErrorModal,
      handleCloseRemoveErrorModal,
      handleRemoveError,
      isRemoveBehavioralWitnessModal,
      handleCloseRemoveBehavioralWitnessModal,
      handleRemoveBehavioralWitness,
      changeStatusLoading,
      hasItemPermission,
      isChangeStatusConfirmModal,
      handleCloseChangeStatusConfirmModal,
      handleSubmitChangeStatusConfirmModal,
      handleChangeIsSelect,
      hasSample,
      viewOnly,
    };
  },
};
</script>
